import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut } from "lucide-react";

/**
 * Public top-level navbar used on the marketing landing page.
 *
 * Layout:
 *  [logo]                [Features][How it works][Pricing][Contact]                [Login][Register School]
 *
 * The center nav links are anchors (#features, #how-it-works, #pricing, #contact)
 * so they smooth-scroll to the matching sections on the LandingPage.
 */
const NAV_LINKS = [
  { href: "#features",     label: "Features" },
  { href: "#how-it-works", label: "How it works" },
  { href: "#pricing",      label: "Pricing" },
  { href: "#contact",      label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate("/", { replace: true }); };

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200/60"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center shrink-0" data-testid="navbar-logo">
          <img
            src="/brand/logo-day.png"
            alt="SchoolBoss"
            className="h-11 w-auto select-none"
            draggable="false"
          />
        </Link>

        {/* Center nav (desktop only) */}
        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-[15px] font-semibold text-slate-700 hover:text-[#2563EB] transition-colors"
              data-testid={`nav-${l.label.replace(/\s/g, "-").toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {user ? (
            <Button
              onClick={handleLogout}
              variant="outline"
              className="rounded-full border-slate-200 text-slate-700 hover:bg-slate-50"
              data-testid="navbar-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          ) : (
            <>
              <Link to="/login" data-testid="navbar-login-link">
                <Button
                  variant="ghost"
                  className="rounded-full text-slate-700 hover:bg-slate-100 font-semibold h-10 px-5"
                >
                  Login
                </Button>
              </Link>
              <Link to="/register" data-testid="navbar-register-link">
                <Button className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold h-10 px-5 shadow-floating">
                  Register School
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
